/**
 * The queue as it is. A dead-lettered job shows the error it died on, because "failed"
 * without the reason sends the operator to the server logs.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useListQuery } from "../lib/useListQuery";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { ago } from "../lib/format";
import type { JobList } from "../lib/types";
import { Shell } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Pagination, usePagination, cursorControl } from "../components/Pagination";
import {
  Button,
  Chip,
  Empty,
  Eyebrow,
  Failed,
  Loading,
  Panel,
  Segmented,
  STATUS_TONE,
} from "../components/ui";

const STATES = ["all", "queued", "running", "succeeded", "failed", "dead"] as const;
type Filter = (typeof STATES)[number];

export function Jobs() {
  // The URL is the filter state, the same as every other list screen: a filtered queue
  // is a link somebody can send.
  const query = useListQuery();
  const navigate = useNavigate();
  const filter = (query.get("state") ?? "all") as Filter;
  const [size, setSize] = useState(50);
  const pager = usePagination();

  const changeFilter = (next: Filter) => {
    query.set({ state: next === "all" ? null : next });
    pager.first();
  };

  const changeSize = (next: number) => {
    setSize(next);
    pager.first();
  };

  const listing = useQuery({
    queryKey: ["jobs", "list", filter, size, pager.cursor],
    queryFn: () =>
      get<JobList>(
        `/v1/jobs?limit=${size}${filter === "all" ? "" : `&state=${filter}`}${
          pager.cursor ? `&cursor=${encodeURIComponent(pager.cursor)}` : ""
        }`,
      ),
    refetchInterval: 5_000,
  });

  const jobs = listing.data?.jobs ?? [];

  return (
    <Shell fill crumb={<span className="text-text">jobs</span>}>
      <div className="shrink-0">
        <Eyebrow n="02">queue</Eyebrow>
        <h2 className="mt-2 text-[1.5rem] leading-none tracking-tighter uppercase">Jobs</h2>
        <p className="mt-2 max-w-[64ch] text-[12.5px] leading-relaxed text-dim">
          One job per document. Retries back off; three failures dead-letter, and the reason is
          kept rather than logged and lost.
        </p>
      </div>

      <Panel
        fill
        title="pipeline"
        actions={
          <>
            <Pagination
              control={cursorControl(pager, listing.data?.next_cursor)}
              size={{ value: size, options: [25, 50, 100], onChange: changeSize }}
            />
            <Segmented options={STATES} value={filter} onChange={changeFilter} />
          </>
        }
      >
        {listing.isPending ? (
          <Loading what="jobs" />
        ) : listing.isError ? (
          <Failed error={listing.error} what="jobs" />
        ) : jobs.length === 0 ? (
          <Empty
            title={filter === "all" ? "queue empty" : `no ${filter} jobs`}
            hint={
              filter === "all"
                ? "Upload a document and a worker picks it up on its next poll."
                : "The queue is not empty — this state is."
            }
            action={
              filter === "all"
                ? { label: "upload a document", onClick: () => navigate("/documents") }
                : { label: "show every state", onClick: () => changeFilter("all") }
            }
          />
        ) : (
          <DataTable
            fill
            table="jobs"
            rows={jobs}
            rowKey={(job) => job.id}
            columns={[
              {
                key: "job",
                header: "job",
                width: 110,
                cellClass: "font-mono text-[11.5px]",
                render: (job) => job.id.slice(0, 8),
              },
              {
                key: "kind",
                header: "kind",
                width: 100,
                cellClass: "font-mono text-[11.5px] text-dim",
                render: (job) => job.kind,
              },
              {
                key: "state",
                header: "state",
                width: 130,
                render: (job) => (
                  <Chip tone={STATUS_TONE[job.state] ?? "mut"} dot>
                    {job.state}
                  </Chip>
                ),
              },
              {
                key: "attempts",
                header: "attempts",
                width: 100,
                align: "end",
                cellClass: "tnum font-mono text-[11.5px]",
                render: (job) => `${job.attempts}${job.max_attempts ? ` / ${job.max_attempts}` : ""}`,
              },
              {
                key: "document",
                header: "document",
                width: 130,
                render: (job) =>
                  job.document_id ? (
                    <Link
                      to={`/documents/${job.document_id}`}
                      className="font-mono text-[11.5px] text-faint hover:text-gold"
                    >
                      {job.document_id.slice(0, 8)} →
                    </Link>
                  ) : (
                    <span className="text-faint">—</span>
                  ),
              },
              {
                key: "updated",
                header: "updated",
                width: 130,
                cellClass: "text-[12px] text-dim",
                render: (job) => ago(job.updated_at),
              },
              {
                key: "error",
                header: "error",
                width: 340,
                render: (job) =>
                  job.last_error ? (
                    <span className="font-mono text-[11px] break-words text-crit">{job.last_error}</span>
                  ) : (
                    <span className="text-faint">—</span>
                  ),
              },
            ]}
          />
        )}
      </Panel>

      <Button variant="ghost" onClick={() => void listing.refetch()} className="shrink-0 self-start">
        refresh now
      </Button>
    </Shell>
  );
}
